import React, { useEffect, useState } from 'react';
import RtlStyles from 'i18n/rtl';
import { ModalBox } from 'components/checkoutModals/common';
import Button, { ButtonTypeEnum } from 'ui/Button/Button';
import styled from 'styled-components';
import Modal, { ModalFooter, ModalHeader, Prompt } from '@ingka/modal';

import '@ingka/svg-icon/dist/style.css';
import '@ingka/button/dist/style.css';
import '@ingka/modal/dist/style.css';
import '@ingka/focus/dist/style.css';

const Images = styled.div`
  max-height: 500px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  overflow-y: scroll;
  grid-gap: 2rem;
`;

type Image = { name: string; url: string; error: boolean };

function ThumbnailPreviewModal({
  images,
  visible,
  hideModal,
  onDownload
}: {
  images: Image[];
  visible: boolean;
  hideModal: () => void;
  onDownload: (includedEntries: string[]) => void;
}) {
  const [selected, setSelected] = useState<Image[]>([]);
  const [downloadQueue, setDownloadQueue] = useState<Image[]>([]);

  useEffect(() => {
    const imagesWithoutErrors = images.filter(img => !img.error);
    setSelected(imagesWithoutErrors);
  }, [images]);

  const onSelect = (image: Image) => {
    const isSelected = !!selected.find(i => i.url === image.url);
    if (isSelected) {
      setSelected(selected.filter(i => i.url !== image.url));
    } else {
      setSelected([...selected, image]);
    }
  };

  const downloadImage = async (image: Image) => {
    const img = await fetch(image.url);
    const imageBlog = await img.blob();
    const imageURL = URL.createObjectURL(imageBlog);

    const link = document.createElement('a');
    link.href = imageURL;
    link.download = image.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadSelected = () => {
    setDownloadQueue(selected);
  };

  const onDone = () => {
    downloadSelected();
    onDownload(selected.map(s => s.name));
    hideModal();
  };

  useEffect(() => {
    if (downloadQueue.length > 0) {
      const nextImage = downloadQueue[0];
      setTimeout(() => {
        downloadImage(nextImage);
        setDownloadQueue(downloadQueue.filter(i => i !== nextImage));
      }, 1000);
    }
  }, [downloadQueue]);

  return (
    <Modal visible={visible} escapable={true} handleCloseBtn={hideModal}>
      <Prompt
        aria-labelledby="VerifyImagesPrompt"
        title={'Verify images'}
        titleId={'VerifyImagesPrompt'}
        header={<ModalHeader ariaCloseTxt="Close prompt" />}
        footer={
          <ModalFooter>
            <Button
              mr="md"
              text={'Download'}
              onClick={onDone}
              type={ButtonTypeEnum.emphasised}
            ></Button>
            <Button text={'Continue'} onClick={hideModal} type={ButtonTypeEnum.secondary}></Button>
          </ModalFooter>
        }
      >
        <RtlStyles>
          <ModalBox>
            <Images>
              {images.map((image, i) => (
                <div
                  onClick={() => onSelect(image)}
                  onKeyPress={() => onSelect(image)}
                  tabIndex={i}
                  role="button"
                  key={`${image.name}-${i}`}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    padding: '10px',
                    boxShadow: selected.find(i => i.url === image.url)
                      ? 'inset 0px 0px 14px 2px blue'
                      : image.error
                      ? 'inset 0px 0px 14px 2px red'
                      : 'none'
                  }}
                >
                  <img
                    src={image.url}
                    alt={image.name}
                    style={{ objectFit: 'contain', height: '300px', width: '100%' }}
                  />
                </div>
              ))}
            </Images>
          </ModalBox>
        </RtlStyles>
      </Prompt>
    </Modal>
  );
}

export default ThumbnailPreviewModal;
